import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import {
  Loader2,
  Mail,
  Phone,
  MapPin,
  User,
  Briefcase,
  GraduationCap,
  Award,
  CalendarDays,
  IndianRupee,
  Clock,
  Globe,
  FileText,
  Calendar,
  Edit,
} from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useProtectedResumeProfile } from 'src/hooks/useResumeData';
import { getUser } from 'src/utils/authStorage';
// import { generatePDF } from './resumeUtils';
import ResumePDF from './ResumePDF';

const MONTH_ABBREVIATIONS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatMonthFromNumeric = (monthValue) => {
  if (monthValue === undefined || monthValue === null) return '';
  const numericMonth =
    typeof monthValue === 'string' ? parseInt(monthValue, 10) : monthValue;
  if (Number.isNaN(numericMonth) || numericMonth < 1 || numericMonth > 12) {
    return String(monthValue);
  }
  return MONTH_ABBREVIATIONS[numericMonth - 1];
};

function formatNoticePeriodBadge(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  if (n === 0) return 'Immediate';
  if (n === 90) return '90+';
  return `${n} days`;
}

// Formats a date-like input into "27 jun 2025". Falls back to raw input if invalid.
const formatDateHuman = (input) => {
  if (!input) return '';
  // Handle plain YYYY-MM-DD safely to avoid timezone shifts
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [y, m, d] = input.split('-').map((v) => parseInt(v, 10));
    const date = new Date(y, m - 1, d);
    const day = date.getDate();
    const month = MONTH_ABBREVIATIONS[date.getMonth()].toLowerCase();
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return String(input);
  const day = date.getDate();
  const month = MONTH_ABBREVIATIONS[date.getMonth()].toLowerCase();
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const Card = ({ children, icon, title }) => (
  <div className="rounded-lg border bg-card text-card-foreground shadow-sm border-blue-100 p-4 h-full flex flex-col">
    <div className="flex items-center gap-2 mb-2">
      <span className="text-ah-brand-icon bg-ah-surface-icon-bg rounded-lg p-2">
        {' '}
        {icon}
      </span>
      <h3 className="text-sm font-semibold ">{title}</h3>
    </div>
    {children}
  </div>
);

export const ResumePreview = () => {
  const navigate = useNavigate();

  // Get candidateId from stored user
  const candidateId = React.useMemo(() => {
    const userData = getUser();
    return userData?.cid || null;
  }, []);

  // Use React Query to fetch resume profile
  const {
    data: resumeData,
    isLoading,
    error,
  } = useProtectedResumeProfile(candidateId);

  useEffect(() => {
    // Only run if not loading and resumeData is available
    if (!isLoading && resumeData?.otherDetails) {
      if (resumeData.otherDetails.is_complete === false) {
        navigate('/submit-resume');
      }
    }
  }, [isLoading, resumeData, navigate]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-full bg-gray-100">
        <Loader2 className="h-8 w-8 animate-spin ahBlueText" />
        <p className="mt-4 text-sm text-gray-600">Loading resume data...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-full bg-gray-100">
        <p className="mt-4 text-sm text-red-600">
          {error.message || 'Error loading resume data'}
        </p>
      </div>
    );
  }
  if (!resumeData) {
    return <div>No resume data found. Please fill the form first.</div>;
  }
  const hasMultipleExperiences = resumeData.workExperiences?.length > 1;
  return (
    <div className="flex h-[calc(100dvh-3.5rem)] w-full justify-center items-start overflow-y-auto bg-gradient-to-b from-blue-50 via-white to-blue-50">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-md bg-blue-100 text-blue-700">
              <User className="h-7 w-7 text-blue-500" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                  {resumeData.firstname} {resumeData.lastname}
                </h1>
              </div>
              {resumeData.workExperiences && (
                <p className="mt-1 text-xs text-muted-foreground max-w-prose text-left">
                  {resumeData.workExperiences[0]?.role}
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-blue-900">
                {resumeData.stateLabel && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-ah-brand-accent" />
                    {typeof resumeData.stateLabel === 'string'
                      ? resumeData.stateLabel.toLowerCase()
                      : String(resumeData.stateLabel)}
                    , IN
                  </span>
                )}
                {resumeData.otherDetails.total_work_experience && (
                  <span className="inline-flex items-center gap-1">
                    <Briefcase className="h-4 w-4 text-ah-brand-accent" />
                    {(
                      resumeData.otherDetails.total_work_experience / 12
                    ).toFixed(1)}{' '}
                    yrs exp
                  </span>
                )}
                {resumeData.noticePeriod != null &&
                  resumeData.noticePeriod !== '' &&
                  !Number.isNaN(Number(resumeData.noticePeriod)) &&
                  Number(resumeData.noticePeriod) >= 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-4 w-4 text-ah-brand-accent " />
                      Notice: {formatNoticePeriodBadge(resumeData.noticePeriod)}
                    </span>
                  )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 mb-4">
            <PDFDownloadLink
              document={<ResumePDF data={resumeData} />}
              fileName={`${resumeData.firstname}_${resumeData.lastname}_resume.pdf`}
              className="no-underline"
            >
              {({ loading }) => (
                <Button
                  size="sm"
                  disabled={loading}
                  className="bg-primary text-white hover:bg-ah-brand-primary-hover text-xs rounded-lg border shadow-sm border-blue-100"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <FileText className="h-3 w-3 mr-2" />
                      Download Resume
                    </>
                  )}
                </Button>
              )}
            </PDFDownloadLink>
            <Button
              variant="outline"
              size="sm"
              className="text-xs rounded-lg border bg-card text-card-foreground shadow-sm border-blue-100 p-4"
              onClick={() =>
                navigate('/submit-resume', { state: { formData: resumeData } })
              }
            >
              <Edit className="h-3 w-3 mr-2" />
              Edit
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Card icon={<Globe className="h-5 w-5" />} title="Contact">
            <div className="flex flex-col gap-2 justify-between h-full">
              <div className="flex flex-col gap-2 text-blue-900 text-xs ml-2">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span>{resumeData.contact || 'N/A' || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span>{resumeData.email || 'N/A' || '-'}</span>
                </div>
                <div className="flex flex-col text-left col-span-2">
                  <span className="flex items-start gap-2 text-xs min-w-0">
                    <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                    <span className="flex flex-col min-w-0">
                      <div className="break-words">
                        {resumeData.addressLine1 || 'N/A' || '-'},{' '}
                        {resumeData.street || 'N/A' || '-'}
                      </div>

                      <span className="text-xs text-gray-500">
                        {resumeData.cityLabel || 'N/A' || '-'},{' '}
                        {resumeData.stateLabel || 'N/A' || '-'}
                      </span>
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </Card>
          <Card icon={<FileText className="h-5 w-5" />} title="Compensation">
            {resumeData.currentCTC && resumeData.expectedCTC ? (
              <div className="grid grid-cols-2 ml-2">
                <div className="flex flex-col text-left">
                  <span className="text-ah-xs-label">Current CTC</span>
                  <span className="flex items-center gap-1 text-xs font-semibold">
                    <IndianRupee className="h-3 w-3" />
                    {resumeData.currentCTC / 100000 || 'N/A' || '-'}
                    LPA
                  </span>
                </div>
                <div className="flex flex-col text-left">
                  <span className="text-ah-xs-label">Expected CTC</span>
                  <span className="flex items-center gap-1 text-xs font-semibold">
                    <IndianRupee className="h-3 w-3" />
                    {resumeData.expectedCTC / 100000 || 'N/A' || '-'}LPA
                  </span>
                </div>
              </div>
            ) : (
              <span className="inline-flex items-center text-xs text-gray-500 italic">
                No compensation added yet
              </span>
            )}
          </Card>
          <Card
            icon={<Calendar className="h-5 w-5" />}
            title="Personal Details"
          >
            <div className="grid grid-cols-1 gap-2 ml-2 text-blue-900">
              <div className="flex flex-col text-left">
                <span className="text-ah-xs-label text-gray-500">
                  Father&apos;s Name
                </span>
                <span className="flex items-start gap-1 text-xs ">
                  <User className="h-4 w-4" />
                  {resumeData.fathersname || 'N/A' || '-'}
                </span>
              </div>
              <div className="flex flex-col text-left">
                <span className="text-ah-xs-label text-gray-500">DOB</span>
                <span className="flex items-center gap-1 text-xs ">
                  <CalendarDays className="h-4 w-4" />
                  {resumeData.dob ? formatDateHuman(resumeData.dob) : 'N/A'}
                </span>
              </div>
              <div className="flex flex-col text-left">
                <span className="text-ah-xs-label text-gray-500">
                  {' '}
                  Last Updated
                </span>
                <span className="flex items-center gap-1 text-xs ">
                  <CalendarDays className="h-4 w-4" />
                  {resumeData.updatedAt
                    ? formatDateHuman(resumeData.updatedAt)
                    : 'N/A'}
                </span>
              </div>
            </div>
          </Card>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-4">
          <Card icon={<User className="h-5 w-5" />} title="Key Skills">
            <div className="flex flex-wrap gap-2">
              {resumeData.skills?.map((skill) => (
                <span
                  key={skill.skill_id ?? skill.skill_name}
                  className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-900 border border-blue-100"
                >
                  {skill.skill_name}
                </span>
              ))}
              {!resumeData.skills?.length && (
                <span className="inline-flex items-center text-xs text-gray-500 italic">
                  No skills added yet
                </span>
              )}
            </div>
          </Card>
          <Card icon={<GraduationCap className="h-5 w-5" />} title="Education">
            <div className="text-left ml-2">
              {resumeData.educations?.map((education) => (
                <div
                  key={`education-${education.institution}`}
                  className="mb-2"
                >
                  <h3 className="text-sm font-semibold">
                    {education.qualificationLabel}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {education.institution} • {education.graduationYear}
                  </p>
                </div>
              ))}
            </div>
          </Card>
          <Card
            icon={<Briefcase className="h-5 w-5" />}
            title="Work Experience"
          >
            <div className="relative text-left ml-2">
              {resumeData.workExperiences?.length ? (
                resumeData.workExperiences.map((experience) => (
                  <div
                    key={`experience-${experience.company}`}
                    className={`relative mb-4 ${
                      hasMultipleExperiences
                        ? "pl-6 after:content-[''] after:absolute after:left-2 after:top-4 after:-bottom-6 after:w-[1px] after:bg-blue-200 last:after:hidden"
                        : ''
                    }`}
                  >
                    {hasMultipleExperiences && (
                      <span className="absolute left-2 top-2 h-2 w-2 -translate-x-1/2 rounded-full bg-ah-brand-icon" />
                    )}
                    <h3 className="text-sm font-semibold">{experience.role}</h3>

                    <span className="text-xs font-semibold text-gray-500 ">
                      {experience.company}
                    </span>
                    <p className="text-xs text-gray-500">
                      {formatMonthFromNumeric(
                        experience.employment_period.startMonth
                      )}{' '}
                      {experience.employment_period.startYear} -{' '}
                      {experience.employment_period.present
                        ? 'Present'
                        : `${formatMonthFromNumeric(
                            experience.employment_period.endMonth
                          )} ${experience.employment_period.endYear}`}
                    </p>
                  </div>
                ))
              ) : (
                <span className="inline-flex items-center text-xs text-gray-500 italic">
                  No work experience added yet
                </span>
              )}
            </div>
          </Card>

          <Card icon={<Award className="h-5 w-5" />} title="Certifications">
            <div className="flex flex-wrap gap-2">
              {(resumeData.certifications || []).map((cert) => (
                <span
                  key={cert.certification_id || cert.name}
                  className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-900 border border-blue-100"
                >
                  {cert.name}
                </span>
              ))}
              {!resumeData.certifications?.length && (
                <span className="inline-flex items-center text-xs text-gray-500 italic">
                  No certifications added yet
                </span>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResumePreview;
